import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useServices } from "../app/services.ts";
import { avatarUrl } from "../data/avatars.ts";
import { label, partner, subtitle, title, VOXIE_ID } from "../data/display.ts";
import { useStore } from "../data/store.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { Icon } from "../ui/Icon.tsx";
import { Composer } from "./Composer.tsx";
import { isSelf } from "../data/display.ts";
import { errorText, useCall } from "../calls/controller.ts";
import { Message } from "./Message.tsx";
import { PlaybackBar } from "./PlaybackBar.tsx";
import styles from "./Conversation.module.css";

/** One conversation: header, playback bar, messages, composer (docs/web-designs screen 2). */
export function Conversation({ id, focusMessage, onTopic, onBack, onBoards, onOpenBoard, onDocuments, onOpenDocument }: { id: string; focusMessage?: string; onTopic: (topicId: string) => void; onBack: () => void; onBoards: () => void; onOpenBoard: (boardId: string) => void; onDocuments: () => void; onOpenDocument: (id: string) => void }) {
  const services = useServices();
  const { conversation, messages, me, online, loaded, typing } = useStore((s) => ({
    conversation: s.conversations.get(id),
    messages: s.messagesOf(id),
    me: s.me,
    online: s.online,
    loaded: s.loaded.has(id),
    typing: (s.activity.get(id) ?? []).filter((a) => a.until > Date.now() && a.user !== s.me),
  }));
  const list = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string>();
  const [error, setError] = useState<string>();
  const [dropped, setDropped] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const p = conversation ? partner(conversation, me) : undefined;
  const call = useCall(services.calls);
  const [callError, setCallError] = useState<string>();
  const running = services.calls.running.get(id);
  const runningHere = conversation?.kind === "group" && running && running.state !== "ended" && call?.callId !== running.id ? running : undefined;

  async function startCall(kind: "voice" | "video") {
    setCallError(undefined);
    // Before ringing someone who cannot be rung: say so, as the app does.
    if (conversation?.kind === "direct" && p) {
      const r = await services.session.call<{ reachable: boolean; reachable_from?: string | null }>(`/v1/users/${p.id}/reachability`).catch(() => null);
      if (r && !r.reachable) {
        const from = r.reachable_from ? ` Wieder anrufbar ab ${new Date(r.reachable_from).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })}.` : "";
        setCallError(`${label(p)} ist gerade nicht anrufbar.${from} Schick bis dahin lieber eine Sprachnachricht.`);
        return;
      }
    }
    await services.calls.start(id, kind, conversation?.kind === "agent").catch((e) => setCallError(errorText(e)));
  }

  useEffect(() => {
    setError(undefined);
    services.sync.loadMessages(id).catch(() => setError("Die Nachrichten ließen sich nicht laden."));
    void services.sync.loadTopics(id).catch(() => undefined);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (p) void avatarUrl(services.session, p.id, p.avatar).then(setSrc);
  }, [p?.id, p?.avatar]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live locations: watch them while the chat is open (the sharers send more often then), and
  // show the newest position each message already carries.
  const liveShares = messages.filter((m) => m.response.live?.active).map((m) => m.response.id).join(",");
  useEffect(() => {
    for (const m of services.store.messagesOf(id)) if (m.response.live?.position) void services.onPosition(id, m.response.id, m.response.live.position, Date.parse(m.response.live.position_at ?? "") || 0);
    if (!liveShares) return;
    const watch = () => services.realtime.send({ t: "watch", conversation: id });
    watch();
    const t = setInterval(watch, 60_000);
    return () => {
      clearInterval(t);
      services.realtime.send({ t: "unwatch", conversation: id });
    };
  }, [id, liveShares, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Read while it is open and visible, as the app does on resume and on every new message.
  const unread = conversation?.unread_count ?? 0;
  useEffect(() => {
    const mark = () => document.visibilityState === "visible" && void services.markRead(id);
    if (unread > 0 || messages.length > 0) mark();
    document.addEventListener("visibilitychange", mark);
    return () => document.removeEventListener("visibilitychange", mark);
  }, [id, unread, messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opens at the bottom (or at the message a search hit points to) and stays there for new ones.
  const stick = useRef(true);
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    if (focusMessage) {
      el.querySelector(`[data-message="${focusMessage}"]`)?.scrollIntoView({ block: "center" });
      return;
    }
    if (stick.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, loaded, focusMessage, id]);

  if (!conversation) return <main className={styles.empty}>Chat nicht gefunden.</main>;
  const name = title(conversation, me);
  let lastDay = "";
  return (
    <main
      className={dragging ? `${styles.conversation} ${styles.dragging}` : styles.conversation}
      aria-label={name}
      onDragOver={(e) => {
        if (![...e.dataTransfer.types].includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        setDropped([...e.dataTransfer.files]);
      }}
    >
      <header className={styles.head}>
        <button className={styles.back} onClick={onBack} aria-label="Zurück">
          <Icon name="back" />
        </button>
        <Avatar id={p?.id ?? conversation.id} name={name} src={src} online={Boolean(p && p.id !== me && online.has(p.id))} voxie={conversation.kind === "agent" || p?.id === VOXIE_ID} />
        <div className={styles.title}>
          <b>{name}</b>
          <span className={typing.length ? styles.live : undefined}>{typing.length ? (typing[0]!.what === "recording" ? "nimmt auf …" : "schreibt …") : subtitle(conversation, me, online)}</span>
        </div>
        <button className={styles.headButton} onClick={onDocuments} aria-label="Dokumente" title="Dokumente">
          <Icon name="doc" />
        </button>
        <button className={styles.headButton} onClick={onBoards} aria-label="Canvas" title="Canvas">
          <Icon name="board" />
        </button>
        {!isSelf(conversation) && (
          <>
            <button className={styles.headButton} onClick={() => void startCall("voice")} aria-label={conversation.kind === "agent" ? "Voxie anrufen" : "Sprachanruf"} title={conversation.kind === "agent" ? "Voxie anrufen" : "Sprachanruf"}>
              <Icon name="call" />
            </button>
            {conversation.kind !== "agent" && (
              <button className={styles.headButton} onClick={() => void startCall("video")} aria-label="Videoanruf" title="Videoanruf">
                <Icon name="video" />
              </button>
            )}
          </>
        )}
      </header>
      {runningHere && (
        <div className={styles.callBanner}>
          <Icon name="call" size={18} /> {runningHere.kind === "video" ? "Gruppen-Videoanruf läuft" : "Gruppenanruf läuft"}
          <button onClick={() => void services.calls.join(id)}>Beitreten</button>
        </div>
      )}
      {callError && <p className={styles.callError}>{callError}</p>}
      <PlaybackBar />
      <div
        className={styles.messages}
        ref={list}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        {!loaded && messages.length === 0 && !error && <p className={styles.note}>Nachrichten werden geladen …</p>}
        {error && <p className={styles.note}>{error}</p>}
        {loaded && messages.length === 0 && <p className={styles.note}>Noch keine Nachrichten.</p>}
        {messages.filter((m) => !(m.response.board_id && m.response.kind === "image")).map((m) => {
          const day = new Date(m.response.created_at).toLocaleDateString("de-AT", { weekday: "long", day: "numeric", month: "long" });
          const separator = day !== lastDay;
          lastDay = day;
          return (
            <div key={m.response.id} className={styles.item}>
              {separator && <div className={styles.day}>{day}</div>}
              <Message message={m} conversation={conversation} onTopic={onTopic} onOpenBoard={onOpenBoard} onOpenDocument={onOpenDocument} />
            </div>
          );
        })}
      </div>
      <Composer conversationId={id} dropped={dropped} onDroppedTaken={() => setDropped([])} />
    </main>
  );
}
